// The signed-in owner's pick queue. Held here rather than inside QueuePanel
// because the player list needs it too — a row has to know whether it is
// already queued.
//
// Every mutation returns the authoritative order, so the server response is
// what lands in state; the draft's my_queue only reseeds it after a refetch.

import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../lib/api'
import type { QueueResponse } from '../types/protocol'

export interface QueueControls {
  queue: number[]
  queueError: string | null
  queueBusy: boolean
  addToQueue: (playerId: number) => Promise<void>
  removeFromQueue: (playerId: number) => Promise<void>
  reorderQueue: (playerIds: number[]) => Promise<void>
}

export function useQueue(
  code: string,
  teamToken: string | null,
  serverQueue: number[] | undefined,
): QueueControls {
  const [queue, setQueue] = useState<number[]>(serverQueue ?? [])
  const [queueError, setQueueError] = useState<string | null>(null)
  const [queueBusy, setQueueBusy] = useState(false)

  // Reseed only when the server's own view actually differs, so an in-flight
  // reorder isn't undone by an unrelated state refetch.
  const serverKey = serverQueue === undefined ? null : serverQueue.join(',')
  useEffect(() => {
    if (serverKey === null) return
    setQueue((prev) => (prev.join(',') === serverKey ? prev : serverKey === '' ? [] : serverKey.split(',').map(Number)))
  }, [serverKey])

  const mutate = useCallback(
    async (path: string, options: { method: 'POST' | 'DELETE' | 'PUT'; body?: unknown }) => {
      if (!teamToken) return
      setQueueBusy(true)
      setQueueError(null)
      try {
        const response = await api<QueueResponse>(`/drafts/${code}${path}`, {
          ...options,
          teamToken,
        })
        setQueue(response.queue)
      } catch (e) {
        setQueueError(e instanceof ApiError ? e.message : 'Could not update your queue')
      } finally {
        setQueueBusy(false)
      }
    },
    [code, teamToken],
  )

  const addToQueue = useCallback(
    (playerId: number) => mutate('/queue/entries', { method: 'POST', body: { player_id: playerId } }),
    [mutate],
  )

  const removeFromQueue = useCallback(
    (playerId: number) => mutate(`/queue/entries/${playerId}`, { method: 'DELETE' }),
    [mutate],
  )

  const reorderQueue = useCallback(
    (playerIds: number[]) => {
      // Show the new order straight away; the response confirms it.
      setQueue(playerIds)
      return mutate('/queue/order', { method: 'PUT', body: { player_ids: playerIds } })
    },
    [mutate],
  )

  return { queue, queueError, queueBusy, addToQueue, removeFromQueue, reorderQueue }
}
